import { useCallback } from 'react'
import { flushSync } from 'react-dom'

// start polyfill
// older versions of typescript do not declare the ViewTransition API
// so we need to polyfill it - if all users of popcorn have updated, we can safely remove this
// type polyfill

/** [MDN Reference](https://developer.mozilla.org/docs/Web/API/ViewTransition) */
interface ViewTransition {
  /** [MDN Reference](https://developer.mozilla.org/docs/Web/API/ViewTransition/finished) */
  readonly finished: Promise<undefined>
  /** [MDN Reference](https://developer.mozilla.org/docs/Web/API/ViewTransition/ready) */
  readonly ready: Promise<undefined>
  /** [MDN Reference](https://developer.mozilla.org/docs/Web/API/ViewTransition/updateCallbackDone) */
  readonly updateCallbackDone: Promise<undefined>
  /** [MDN Reference](https://developer.mozilla.org/docs/Web/API/ViewTransition/skipTransition) */
  skipTransition(): void
}

interface ViewTransitionUpdateCallback {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (): any
}

declare global {
  interface Document {
    startViewTransition(
      callbackOptions?: ViewTransitionUpdateCallback,
    ): ViewTransition
  }
}

// end polyfill

/**
 * Hook to use View Transition API in React components. If the browser does not support it, the callback
 * is executed immediately.
 *
 * @example
 * ```tsx
 * const transition = useViewTransition()
 *
 * transition(() => {
 *  setState('new state')
 * })
 * ```
 */
export function useViewTransition() {
  const startViewTransition = useCallback((callback: () => void) => {
    if (!document.startViewTransition) {
      callback()
      return
    }

    document.startViewTransition(() => {
      flushSync(callback)
    })
  }, [])

  return startViewTransition
}
